import { Col, Radio, Row, Space, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import useSmartField from 'hooks/useSmartField';
import FormField from 'components/FormField';
import styled from '@emotion/styled';
import { useMemo, useRef } from 'react';
import EndTimeForeverPicker from '@knowledge/knowledges/general/edit/selectors/repeatTime/EndTimeForeverPicker';
import MultipleTimePicker from '@knowledge/knowledges/general/edit/selectors/repeatTime/MultipleTimePicker';
import dayjs from 'lib/dayjs';
import PresetTime from './time/usePresetTime';
import { useTranslate } from '@tolgee/react';

type DtScopeSelectProps = {
  fieldNamePrefix: string;
};

function DtScopeSelect(props: DtScopeSelectProps) {
  const { fieldNamePrefix } = props;
  const { t } = useTranslate();
  const {
    field: { value, onChange },
  } = useSmartField<any>(fieldNamePrefix);
  const { field: filterCodeField, helpers: fieldCodeHelpers } = useSmartField(
    `${fieldNamePrefix}.filterCode`,
  );

  const prevTimeType = useRef('DT_REFERENCE');
  const prevDefine = useRef('DT_SPECIFIED');

  const timeType = useMemo(() => {
    return filterCodeField.value === 'DT_ALL' ? 'DT_ALL' : 'OTHER';
  }, [filterCodeField]);

  const define = useMemo(() => {
    return filterCodeField.value === 'DT_REFERENCE' ? 'DT_REFERENCE' : 'OTHER';
  }, [filterCodeField]);

  return (
    <div>
      <Radio.Group
        value={timeType}
        onChange={(e) => {
          const next = e.target.value === 'DT_ALL' ? 'DT_ALL' : prevTimeType.current;
          onChange({
            ...value,
            filterCode: next,
            specifiedDtRange: next === 'DT_ALL' ? [] : value.specifiedDtRange,
          });
        }}
      >
        <Space direction="vertical">
          <Radio value="DT_ALL">{t('knowledge.长期有效')}</Radio>
          <Radio value="OTHER">{t('knowledge.限定时间段')}</Radio>
        </Space>
      </Radio.Group>
      {timeType === 'OTHER' && (
        <>
          <SelectTimeInfo>
            <Row gutter={9} align="middle">
              <Col>
                <Radio.Group
                  value={define}
                  onChange={(e) => {
                    fieldCodeHelpers.setValue(
                      e.target.value === 'DT_REFERENCE' ? 'DT_REFERENCE' : prevDefine.current,
                    );
                  }}
                >
                  <Radio.Button value="DT_REFERENCE">{t('knowledge.预定义时间段')}</Radio.Button>
                  <Radio.Button value="OTHER">{t('knowledge.自定义时间段')}</Radio.Button>
                </Radio.Group>
              </Col>
              <Col>
                <Tooltip
                  placement="topLeft"
                  title={
                    filterCodeField.value === 'DT_REFERENCE'
                      ? t('knowledge.timeTip1_copy')
                      : t('knowledge.timeTip2')
                  }
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </Col>
            </Row>

            {filterCodeField.value === 'DT_REFERENCE' ? (
              <>
                <FormField
                  name={`${fieldNamePrefix}.referenceIds`}
                  validateTrigger={['onSubmit']}
                  validate={(value) => {
                    if (filterCodeField.value === 'DT_REFERENCE' && value?.length === 0) {
                      return t('请选择预定义时间段');
                    }
                  }}
                >
                  {({ field }) => {
                    return <PresetTime value={field.value} onChange={field.onChange} />;
                  }}
                </FormField>
              </>
            ) : (
              <div className="pr-[12px]">
                <Radio.Group
                  style={{ margin: '8px 0' }}
                  value={filterCodeField.value}
                  onChange={filterCodeField.onChange}
                >
                  <Radio value="DT_SPECIFIED"> {t('knowledge.固定时间段')} </Radio>
                  <Radio value="DT_REPEAT"> {t('knowledge.循环时间段')} </Radio>
                </Radio.Group>

                {filterCodeField.value === 'DT_SPECIFIED' ? (
                  <FormField
                    name={`${fieldNamePrefix}.specifiedDtRange`}
                    validateTrigger={['onSubmit']}
                    validate={(value) => {
                      if (filterCodeField.value === 'DT_SPECIFIED' && !value?.[0]?.startAt) {
                        return t('knowledge.请选择固定时间段');
                      }
                    }}
                  >
                    {({ field, helpers }) => {
                      return (
                        <EndTimeForeverPicker
                          required
                          showTime
                          value={
                            field?.value?.[0]?.startAt
                              ? [
                                  dayjs(field?.value?.[0]?.startAt, 'YYYY-MM-DD HH:mm'),
                                  dayjs(field?.value?.[0]?.endAt, 'YYYY-MM-DD HH:mm'),
                                ]
                              : undefined
                          }
                          onChange={(value) => {
                            const startAt = value?.[0];
                            const endAt = value?.[1];
                            helpers.setValue([
                              {
                                startAt,
                                endAt,
                              },
                            ]);
                          }}
                        />
                      );
                    }}
                  </FormField>
                ) : (
                  <MultipleTimePicker fieldNamePrefix={fieldNamePrefix} />
                )}
              </div>
            )}
          </SelectTimeInfo>
          <SelectTimeInfoTip>{t('knowledge.selectTimeInfoTip')}</SelectTimeInfoTip>
        </>
      )}
    </div>
  );
}

export default DtScopeSelect;

const SelectTimeInfo = styled.div`
  padding: 12px 16px;
  background: var(--fett-erp-card-bg);
  margin-top: 6px;
`;

const SelectTimeInfoTip = styled.div`
  font-size: 12px;
  color: var(--fett-text-tertiary);
  margin-top: 8px;
`;
